import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Plus, ChevronRight, Download } from 'lucide-react'
import { getHome, getRooms, createRoom, updateRoom, deleteRoom, getItems, createItem, getExpenses, createExpense, updateExpense, deleteExpense, downloadItemsCSV, daysUntil } from '../lib/supabase'
import { Modal, Field, Input, Select, Textarea, ConfirmDialog, EmptyState, Loading, DotMenu, StatusBadge, TypeBadge, UrgencyBadge, formatCurrency } from '../components/ui'
import { ROOM_TYPES, FLOOR_LEVELS, ITEM_STATUSES, EXPENSE_TYPES, PAYMENT_PERIODS, ANNUAL_MULTIPLIER } from '../lib/constants'
import { useSettings } from '../lib/settings'
import ItemForm from './ItemForm'

// ── Room form ─────────────────────────────────────────────────────────────────
function RoomForm({ initial = {}, homeId, onSave, onClose }) {
  const [form, setForm] = useState({ name: '', room_type: '', floor_level: '', square_footage: '', notes: '', ...initial, home_id: homeId })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true); try { await onSave(form) } finally { setSaving(false) }
  }
  return (
    <Modal title={initial.id ? 'Edit Space' : 'Add Space'} onClose={onClose}
      footer={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? 'Saving…' : 'Save Space'}</button></>}>
      <Field label="Space Name *"><Input placeholder="e.g. Primary Bedroom" value={form.name} onChange={e => set('name', e.target.value)} /></Field>
      <div className="form-row">
        <Field label="Space Type"><Select value={form.room_type} onChange={e => set('room_type', e.target.value)}><option value="">Select…</option>{ROOM_TYPES.map(t => <option key={t}>{t}</option>)}</Select></Field>
        <Field label="Floor Level"><Select value={form.floor_level} onChange={e => set('floor_level', e.target.value)}><option value="">Select…</option>{FLOOR_LEVELS.map(f => <option key={f}>{f}</option>)}</Select></Field>
      </div>
      <Field label="Notes"><Textarea placeholder="Notes about this space…" value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
    </Modal>
  )
}

// ── Expense form ──────────────────────────────────────────────────────────────
function ExpenseForm({ initial = {}, homeId, onSave, onClose }) {
  const [form, setForm] = useState({ name: '', type: '', provider: '', cost: '', period: 'Annual', next_due: '', doc_url: '', notes: '', ...initial, home_id: homeId })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    const payload = { ...form, cost: form.cost ? parseFloat(form.cost) : null, next_due: form.next_due || null }
    try { await onSave(payload) } finally { setSaving(false) }
  }
  return (
    <Modal title={initial.id ? 'Edit Expense' : 'Add Expense'} onClose={onClose}
      footer={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? 'Saving…' : 'Save Expense'}</button></>}>
      <Field label="Expense Name *"><Input placeholder="e.g. Homeowners Insurance" value={form.name} onChange={e => set('name', e.target.value)} /></Field>
      <div className="form-row">
        <Field label="Type"><Select value={form.type} onChange={e => set('type', e.target.value)}><option value="">Select…</option>{EXPENSE_TYPES.map(t => <option key={t}>{t}</option>)}</Select></Field>
        <Field label="Payment Period"><Select value={form.period} onChange={e => set('period', e.target.value)}>{PAYMENT_PERIODS.map(p => <option key={p}>{p}</option>)}</Select></Field>
      </div>
      <div className="form-row">
        <Field label="Amount"><Input type="number" placeholder="0.00" value={form.cost} onChange={e => set('cost', e.target.value)} /></Field>
        <Field label="Next Due Date"><Input type="date" value={form.next_due} onChange={e => set('next_due', e.target.value)} /></Field>
      </div>
      <Field label="Provider / Payee"><Input placeholder="e.g. State Farm" value={form.provider} onChange={e => set('provider', e.target.value)} /></Field>
      <Field label="Document URL" hint="Link or Google Drive URL"><Input placeholder="https://…" value={form.doc_url} onChange={e => set('doc_url', e.target.value)} /></Field>
      <Field label="Notes"><Textarea placeholder="Policy number, account info, etc." value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
    </Modal>
  )
}

// ── Room card ─────────────────────────────────────────────────────────────────
function RoomCard({ room, homeId, onEdit, onDelete }) {
  const navigate = useNavigate()
  const { showRoomTotals } = useSettings()
  const items = room.items || []
  const total = items.reduce((s, i) => s + (i.cost || 0), 0)
  const open = items.filter(i => i.status === 'Considering' || i.status === 'Shortlisted').length
  return (
    <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/homes/${homeId}/rooms/${room.id}`)}>
      <DotMenu onEdit={onEdit} onDelete={onDelete} />
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, paddingRight: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', letterSpacing: '-.01em' }}>{room.name}</h3>
          <ChevronRight size={15} color="var(--ocean)" />
        </div>
        <div style={{ fontSize: '.75rem', color: 'var(--ocean)', marginBottom: 12 }}>
          {[room.room_type, room.floor_level].filter(Boolean).join(' · ') || <span>&nbsp;</span>}
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <div className="stat-item"><span className="stat-value" style={{ fontSize: '1.125rem' }}>{items.length}</span><span className="stat-label">Items</span></div>
          {showRoomTotals && total > 0 && <div className="stat-item"><span className="stat-value" style={{ fontSize: '1.125rem' }}>{formatCurrency(total)}</span><span className="stat-label">Total</span></div>}
          {open > 0 && <div className="stat-item"><span className="stat-value" style={{ fontSize: '1.125rem' }}>{open}</span><span className="stat-label">Open</span></div>}
        </div>
      </div>
    </div>
  )
}

// ── Expense card ──────────────────────────────────────────────────────────────
function ExpenseCard({ expense, onEdit, onDelete }) {
  const days = daysUntil(expense.next_due)
  const annualAmt = expense.period !== 'One-Time' ? (expense.cost || 0) * (ANNUAL_MULTIPLIER[expense.period] || 1) : null
  return (
    <div className="card">
      <DotMenu onEdit={onEdit} onDelete={onDelete} />
      <div className="card-body" style={{ paddingRight: 40 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', letterSpacing: '-.01em', marginBottom: 6 }}>{expense.name}</h3>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {expense.type && <span className="expense-pill">{expense.type}</span>}
          {expense.period && <span className="expense-pill">{expense.period}</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: '.68rem', color: 'var(--ocean)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>Amount</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.9375rem', fontWeight: 500 }}>{formatCurrency(expense.cost)}</div>
          </div>
          {annualAmt !== null && (
            <div>
              <div style={{ fontSize: '.68rem', color: 'var(--ocean)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>Annual</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.9375rem', fontWeight: 500, color: 'var(--navy)' }}>{formatCurrency(annualAmt)}</div>
            </div>
          )}
        </div>
        {expense.provider && <div style={{ fontSize: '.75rem', color: 'var(--ocean)', marginBottom: 6 }}>{expense.provider}</div>}
        {expense.next_due && <UrgencyBadge days={days} dateStr={expense.next_due} />}
        {expense.notes && <p style={{ fontSize: '.75rem', color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>{expense.notes}</p>}
        {expense.doc_url && (
          <a href={expense.doc_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.75rem', color: 'var(--navy)', marginTop: 8 }}>
            📄 View document
          </a>
        )}
      </div>
    </div>
  )
}

// ── Item card (category view) ─────────────────────────────────────────────────
function ItemCard({ item, room }) {
  return (
    <div className="card">
      <div className="card-body">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '.9375rem', letterSpacing: '-.01em', marginBottom: 3, lineHeight: 1.3 }}>{item.name}</h3>
        {item.brand && <div style={{ fontSize: '.75rem', color: 'var(--ocean)', marginBottom: 8 }}>{item.brand}</div>}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
          <StatusBadge status={item.status} />
          {item.type && <TypeBadge type={item.type} />}
        </div>
        {item.details && <p style={{ fontSize: '.75rem', color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>{item.details}</p>}
        {room && <div style={{ fontSize: '.72rem', color: 'var(--ocean)', marginBottom: 6 }}>📍 {room.name}</div>}
        {item.purchase_url && (
          <a href={item.purchase_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '.75rem', fontWeight: 500, color: 'var(--navy)', background: 'var(--navy-light)', padding: '4px 10px', borderRadius: 99, textDecoration: 'none', marginTop: 4 }}>
            ↗ View item
          </a>
        )}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HomeDetailPage() {
  const { homeId } = useParams()
  const { activeCategories } = useSettings()
  const [home, setHome] = useState(null)
  const [rooms, setRooms] = useState([])
  const [allItems, setAllItems] = useState([])
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [section, setSection] = useState('items') // 'items' | 'expenses'
  const [homeView, setHomeView] = useState('rooms') // 'rooms' | 'categories'
  const [modal, setModal] = useState(null) // null | {type, data}
  const [deleting, setDeleting] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const load = async () => {
    try {
      const [h, r, e] = await Promise.all([getHome(homeId), getRooms(homeId), getExpenses(homeId)])
      setHome(h); setRooms(r); setExpenses(e)
      // Load all items for category view
      const itemArrays = await Promise.all(r.map(room => getItems(room.id)))
      setAllItems(itemArrays.flat())
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [homeId])

  const closeModal = () => setModal(null)

  const handleSaveRoom = async (form) => {
    const payload = {
      ...form,
      home_id: homeId,
      square_footage: form.square_footage ? parseInt(form.square_footage, 10) : null,
    }
    console.log('Saving space with payload:', payload)
    try {
      modal?.data?.id ? await updateRoom(modal.data.id, payload) : await createRoom(payload)
      closeModal(); load()
    } catch (err) {
      console.error('Error saving space:', err)
      alert(`Error saving space: ${err.message}`)
    }
  }
  const handleSaveExpense = async (form) => {
    const payload = { ...form, home_id: homeId }
    console.log('Saving expense with payload:', payload)
    try {
      modal?.data?.id ? await updateExpense(modal.data.id, payload) : await createExpense(payload)
      closeModal(); load()
    } catch (err) {
      console.error('Error saving expense:', err)
      alert(`Error saving expense: ${err.message}`)
    }
  }
  const handleDelete = async () => {
    setDeleteLoading(true)
    try {
      if (deleting.type === 'room') await deleteRoom(deleting.item.id)
      if (deleting.type === 'expense') await deleteExpense(deleting.item.id)
      setDeleting(null); load()
    } finally { setDeleteLoading(false) }
  }

  const handleExportCSV = () => downloadItemsCSV(allItems, rooms, home.name.replace(/\s+/g, '_') + '_items')

  if (loading) return <div className="page-body"><Loading /></div>
  if (!home) return <div className="page-body"><p>Property not found.</p></div>

  const totalAnnualExp = expenses.filter(e => e.period !== 'One-Time').reduce((s, e) => s + (e.cost || 0) * (ANNUAL_MULTIPLIER[e.period] || 1), 0)

  // Group rooms by floor
  const byFloor = {}
  rooms.forEach(r => { const k = r.floor_level || 'Other'; if (!byFloor[k]) byFloor[k] = []; byFloor[k].push(r) })
  const floorOrder = ['Basement','1st Floor','2nd Floor','3rd Floor','Attic','All Floors','Other']
  const sortedFloors = Object.keys(byFloor).sort((a, b) => {
    const ai = floorOrder.indexOf(a); const bi = floorOrder.indexOf(b)
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi)
  })

  return (
    <div className="page-body">
      <div className="breadcrumb">
        <Link to="/">Properties</Link>
        <span className="breadcrumb-sep">›</span>
        <span>{home.name}</span>
      </div>

      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">{home.name}</h1>
          <p className="page-subtitle">{[home.street_address, home.city, home.state].filter(Boolean).join(', ') || 'No address'}</p>
        </div>
        <div className="page-header-actions">
          {section === 'items' && (
            <>
              <div className="view-toggle">
                <button className={`view-toggle-btn${homeView === 'rooms' ? ' active' : ''}`} onClick={() => setHomeView('rooms')}>By Space</button>
                <button className={`view-toggle-btn${homeView === 'categories' ? ' active' : ''}`} onClick={() => setHomeView('categories')}>By Category</button>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={handleExportCSV}><Download size={13} /> CSV</button>
              <button className="btn btn-primary" onClick={() => setModal({ type: homeView === 'categories' ? 'item' : 'room', data: {} })}>
                <Plus size={15} /> {homeView === 'categories' ? 'Add Item' : 'Add Space'}
              </button>
            </>
          )}
          {section === 'expenses' && (
            <button className="btn btn-primary" onClick={() => setModal({ type: 'expense', data: {} })}>
              <Plus size={15} /> Add Expense
            </button>
          )}
        </div>
      </div>

      {/* Page tabs */}
      <div className="page-tabs">
        <button className={`page-tab${section === 'items' ? ' active' : ''}`} onClick={() => setSection('items')}>Spaces & Items</button>
        <button className={`page-tab${section === 'expenses' ? ' active' : ''}`} onClick={() => setSection('expenses')}>
          Expenses
          {expenses.length > 0 && <span style={{ fontSize: '.75rem', color: 'var(--ocean)' }}>({expenses.length})</span>}
        </button>
      </div>

      {/* ── Expenses section ── */}
      {section === 'expenses' && (
        <>
          {totalAnnualExp > 0 && (
            <div style={{ marginBottom: 20, fontFamily: 'var(--font-mono)', fontSize: '.8125rem', color: 'var(--navy)', fontWeight: 500 }}>
              {formatCurrency(totalAnnualExp)} / year in tracked recurring expenses
            </div>
          )}
          {expenses.length === 0 ? (
            <EmptyState icon="📋" title="No expenses yet" description="Track insurance, taxes, HOA, and other recurring costs for this property."
              action={<button className="btn btn-primary" onClick={() => setModal({ type: 'expense', data: {} })}><Plus size={15} /> Add Expense</button>} />
          ) : (
            <div className="grid-expenses">
              {expenses.map(exp => (
                <ExpenseCard key={exp.id} expense={exp}
                  onEdit={() => setModal({ type: 'expense', data: exp })}
                  onDelete={() => setDeleting({ type: 'expense', item: exp })} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Items section — By Space ── */}
      {section === 'items' && homeView === 'rooms' && (
        rooms.length === 0 ? (
          <EmptyState icon="🛋️" title="No spaces yet" description="Add spaces to start tracking finishes, appliances, furniture, and decor."
            action={<button className="btn btn-primary" onClick={() => setModal({ type: 'room', data: {} })}><Plus size={15} /> Add Space</button>} />
        ) : (
          sortedFloors.map(floor => (
            <div key={floor} style={{ marginBottom: 32 }}>
              <div className="section-header">
                <span className="section-title">{floor}</span>
                <span style={{ fontSize: '.75rem', color: 'var(--ocean)' }}>{byFloor[floor].length} space{byFloor[floor].length !== 1 ? 's' : ''}</span>
              </div>
              <div className="grid-rooms">
                {byFloor[floor].map(room => (
                  <RoomCard key={room.id} room={room} homeId={homeId}
                    onEdit={() => setModal({ type: 'room', data: room })}
                    onDelete={() => setDeleting({ type: 'room', item: room })} />
                ))}
              </div>
            </div>
          ))
        )
      )}

      {/* ── Items section — By Category ── */}
      {section === 'items' && homeView === 'categories' && (
        allItems.length === 0 ? (
          <EmptyState icon="📦" title="No items yet" description="Add items to spaces to see them here grouped by category." />
        ) : (
          activeCategories.map(cat => {
            const catItems = allItems.filter(i => i.type === cat.key)
            if (!catItems.length) return null
            return (
              <div key={cat.key} style={{ marginBottom: 32 }}>
                <div className="section-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14 }}>{cat.emoji}</span>
                    <span className="section-title">{cat.key}s</span>
                  </div>
                  <span style={{ fontSize: '.75rem', color: 'var(--ocean)' }}>{catItems.length} item{catItems.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="grid-items">
                  {catItems.map(item => {
                    const room = rooms.find(r => r.id === item.room_id)
                    return <ItemCard key={item.id} item={item} room={room} />
                  })}
                </div>
              </div>
            )
          })
        )
      )}

      {/* ── Modals ── */}
      {modal?.type === 'room' && <RoomForm initial={modal.data} homeId={homeId} onSave={handleSaveRoom} onClose={closeModal} />}
      {modal?.type === 'item' && <ItemForm homeId={homeId} rooms={rooms} onSave={() => { closeModal(); load() }} onClose={closeModal} />}
      {modal?.type === 'expense' && <ExpenseForm initial={modal.data} homeId={homeId} onSave={handleSaveExpense} onClose={closeModal} />}
      {deleting && (
        <ConfirmDialog
          title={`Delete ${deleting.type === 'room' ? 'Space' : 'Expense'}`}
          message={deleting.type === 'room'
            ? `Delete "${deleting.item.name}"? All items in this space will also be deleted.`
            : `Delete "${deleting.item.name}"? This cannot be undone.`}
          onConfirm={handleDelete} onCancel={() => setDeleting(null)} loading={deleteLoading} />
      )}
    </div>
  )
}
